import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BdExportAsFileAsyncData } from '@bindoc-projects/util-domain-lib';
import {
  BehaviorSubject,
  catchError,
  defer,
  forkJoin,
  NEVER,
  Observable,
  of,
} from 'rxjs';
import { BdExportAsFileAsyncConfig } from '../export-config';
import { BdLoaderComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { BdUiLabelsInputComponent } from '@bindoc-projects/labels-lib/ui';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Button, ButtonDirective } from 'primeng/button';
import { IftaLabel } from 'primeng/iftalabel';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import {
  CaseExportTemplateService,
  toCaseExportColumnOptions,
} from '../../case-export-template';
import { BdUiSingleSelectComponent, BdUiSingleSelectConfig } from '@bindoc-projects/ui-lib/selection/ui';
import { BdExportTemplateColumnsFormComponent } from '../../template-controls/export-template-columns-form.component';
import { map, take } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';

export interface BdExportAsFileAsyncDialogData {
  data: BdExportAsFileAsyncData;
  filename: string;
  config: BdExportAsFileAsyncConfig;
}

/** Closed-with result: the export outcome the trigger component turns into a toast. */
export interface BdExportAsFileAsyncDialogResult {
  status: 'ok' | 'error';
}

@Component({
  selector: 'bd-export-as-file-async-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4">
      <bd-loader [loadItem]="loading$" [initTemplate]="formTpl"></bd-loader>
      <div class="flex flex-row justify-end gap-4">
        <button
          pButton
          severity="secondary"
          [text]="true"
          data-test="export-abort"
          (click)="abort()"
        >
          Abbrechen
        </button>
        @if (loadingState() === 0) {
          <button
            pButton
            severity="primary"
            bdDialogPrimaryAction
            [disabled]="form.invalid"
            data-test="export-submit"
            (click)="onExportClick()"
          >
            Exportieren
          </button>
        }
      </div>
    </div>

    <ng-template #formTpl>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          @if (showExportTypePicker) {
            <p-iftalabel>
              <p-select
                inputId="export-type"
                data-test="export-type"
                [options]="exportTypeOptions"
                optionLabel="label"
                optionValue="value"
                fluid
                variant="filled"
                [formControl]="form.controls.exportType"
                appendTo="body"
              ></p-select>
              <label for="export-type">Typ</label>
            </p-iftalabel>
          }
          <p-iftalabel>
            <input
              id="export-file-name"
              data-test="export-file-name"
              pInputText
              fluid
              variant="filled"
              [formControl]="form.controls.fileName"
              autocomplete="off"
            />
            <label for="export-file-name">Dateiname</label>
          </p-iftalabel>
          <bd-ui-labels-input
            [labels]="labels()"
            (labelsChange)="onLabelsChange($event)"
          >
          </bd-ui-labels-input>
        </div>

        @if (exportConfig.exportTemplate) {
          <div>
            <p>Exportierte Spalten</p>
            <p-tabs
              #tabs
              value="template"
              (valueChange)="onTabValueChange($event)"
            >
              <p-tablist>
                <p-tab value="template">Vorlage</p-tab>
                <p-tab value="custom">Benutzerdefinierte Spalten</p-tab>
                <p-tab value="all">Alle Spalten</p-tab>
              </p-tablist>
              <p-tabpanels>
                @if (tabs.value() === 'template') {
                  <p-tabpanel value="template">
                    <div class="flex flex-col gap-2">
                      <bd-ui-single-select
                        [config]="selectConfig"
                        [selectOptions]="exportTemplateOptions$"
                        (selection)="onExportTemplateChange($event)"
                      ></bd-ui-single-select>
                      <!-- Jump to the export-template form to create a new
                           template; closes this dialog so the form is not
                           hidden behind it. -->
                      <p-button
                        class="self-start"
                        label="Neue Vorlage erstellen"
                        severity="secondary"
                        [routerLink]="newTemplateRoute"
                        (onClick)="ref.close()"
                        data-test="export-as-file-async-new-template-link"
                      ></p-button>
                    </div>
                  </p-tabpanel>
                }
                <p-tabpanel value="custom">
                  <bd-export-template-columns-form
                    [columns]="columns()"
                    (propertyIdsChange)="onPropertyIdsChange($event)"
                  ></bd-export-template-columns-form>
                </p-tabpanel>
                <p-tabpanel value="all">
                  <span>Alle verfügbaren Spalten werden exportiert</span>
                </p-tabpanel>
              </p-tabpanels>
            </p-tabs>
          </div>
        }
      </div>
    </ng-template>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    BdLoaderComponent,
    ReactiveFormsModule,
    BdUiLabelsInputComponent,
    BdDataTestDirective,
    ButtonDirective,
    IftaLabel,
    InputText,
    Select,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    BdExportTemplateColumnsFormComponent,
    BdUiSingleSelectComponent,
    RouterLink,
    Button,
  ],
})
export class BdExportAsFileAsyncDialogComponent {
  private readonly _dialogConfig = inject(
    DynamicDialogConfig<BdExportAsFileAsyncDialogData>,
  );
  private readonly _exportService = inject(CaseExportTemplateService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly ref = inject(
    DynamicDialogRef<BdExportAsFileAsyncDialogComponent>,
  );
  public readonly resourceName = ShareableType.CASE_EXPORT_TEMPLATE;

  /** Route to the case-export-template creation form (opened from the "Vorlage" tab). */
  public readonly newTemplateRoute = [
    '/virtual-clinics',
    'case-export-templates',
    'new',
  ];
  public readonly columns = toSignal(
    this._exportService
      .exportProperties()
      .pipe(map((properties) => toCaseExportColumnOptions(properties))),
    { initialValue: [] },
  );
  public readonly selectConfig: BdUiSingleSelectConfig<{
    id: string;
    name: string;
    properties: string[];
  }> = {
    selectLabel: 'Export Vorlage',
    resetOption: false,
    id: 'export-as-file-async-export-template-select',
    option: {
      uniqueFnc: (option) => option.id,
      searchOptionAccessor: (option) => option.name,
      sortFnc: (a, b) => a.name.localeCompare(b.name),
    },
  };

  public readonly labels = signal<string[]>([]);
  public readonly loadingState = signal<BdLoaderState>(BdLoaderState.INIT);
  public readonly loading$ = new BehaviorSubject<BdLoaderState>(
    BdLoaderState.INIT,
  );
  public readonly exportConfig: BdExportAsFileAsyncConfig;
  /** Export formats offered by the current export (defaults to `['xlsx']`). */
  public readonly availableExportTypes: ('csv' | 'xlsx')[];
  /** Whether to render the "Typ" picker — only when more than one format is offered. */
  public readonly showExportTypePicker: boolean;
  public readonly exportTypeOptions: { label: string; value: 'csv' | 'xlsx' }[];
  public readonly form: FormGroup<{
    fileName: FormControl<string>;
    exportProperties: FormControl<string[]>;
    exportType: FormControl<'csv' | 'xlsx'>;
  }>;
  public exportTemplateOptions$: Observable<
    { id: string; name: string; properties: string[] }[]
  > = NEVER;

  private _exportData: BdExportAsFileAsyncData;

  constructor() {
    effect(() => this.loading$.next(this.loadingState()));

    this._exportData = this._dialogConfig.data.data;
    this.exportConfig = this._dialogConfig.data.config;

    this.availableExportTypes = this.exportConfig.form
      ?.availableExportTypes ?? ['xlsx'];
    this.showExportTypePicker = this.availableExportTypes.length > 1;
    const EXPORT_TYPE_LABELS: Record<'csv' | 'xlsx', string> = {
      csv: 'CSV',
      xlsx: 'XLSX (Excel)',
    };
    this.exportTypeOptions = this.availableExportTypes.map((value) => ({
      value,
      label: EXPORT_TYPE_LABELS[value],
    }));

    this.exportTemplateOptions$ = forkJoin([
      this._exportService.defaultTemplates(),
      this._exportService.findAll(),
    ]).pipe(
      map((data) => {
        return [
          ...data[0].map((it) => ({
            id: crypto.randomUUID(),
            name: it.name,
            properties: it.properties,
          })),
          ...data[1].map((it) => ({
            id: it.id,
            name: it.name,
            properties: it.properties,
          })),
        ];
      }),
    );

    this.form = new FormGroup({
      fileName: new FormControl<string>('', [Validators.required]),
      exportProperties: new FormControl<string[]>(
        null,
        this.exportConfig.exportTemplate
          ? [Validators.required, Validators.minLength(1)]
          : [],
      ),
      exportType: new FormControl<'csv' | 'xlsx'>(
        this.availableExportTypes[0] ?? 'xlsx',
        { nonNullable: true },
      ),
    });

    if (this._dialogConfig.data.filename) {
      this.form.controls.fileName.setValue(this._dialogConfig.data.filename);
    }

    if (this.exportConfig.form?.defaultLabels) {
      this.labels.set([...this.exportConfig.form.defaultLabels]);
    }
  }

  public onLabelsChange(labels: string[]): void {
    this.labels.set(labels);
  }

  public onExportTemplateChange(exportTemplate: {
    id: string;
    name: string;
    properties: string[];
  }): void {
    this.form.controls.exportProperties.patchValue(exportTemplate.properties);
  }

  public onPropertyIdsChange(propertyIds: string[]): void {
    this.form.controls.exportProperties.patchValue(propertyIds);
  }

  public onTabValueChange(value: string | number): void {
    switch (value) {
      case 'template': {
        this.form.controls.exportProperties.reset([]);
        this.form.controls.exportProperties.addValidators([
          Validators.required,
          Validators.minLength(1),
        ]);
        break;
      }
      case 'all': {
        this.form.controls.exportProperties.reset([]);
        this.form.controls.exportProperties.clearValidators();
        break;
      }
      case 'custom': {
        this.form.controls.exportProperties.addValidators([
          Validators.required,
          Validators.minLength(1),
        ]);
        this.form.controls.exportProperties.reset([]);
        break;
      }
    }

    this.form.controls.exportProperties.updateValueAndValidity();
  }

  public abort(): void {
    this.ref.close();
  }

  public onExportClick(): void {
    this.loadingState.set(BdLoaderState.LOADING);
    // `defer` turns a synchronous throw inside `trigger()` (e.g. building the
    // request from an unexpected stored filter, BDA-6548) into an error the
    // `catchError` below handles — otherwise it escapes the pipe, no request is
    // sent and the dialog stays in its loading state forever.
    defer(() =>
      this._exportData.trigger(
        this.form.controls.fileName.value,
        this.labels(),
        this._dialogConfig.data?.config.exportTemplate
          ? {
              exportProperties: this.form.controls.exportProperties.value,
            }
          : undefined,
        this.form.controls.exportType.value,
      ),
    )
      .pipe(
        catchError(() => of({ status: 'error' as const })),
        // An export click consumes exactly one result. `take(1)` completes the
        // subscription afterwards and `takeUntilDestroyed` drops it when the
        // dialog closes — without either, a `trigger()` backed by a
        // non-completing source (e.g. a filter BehaviorSubject) stays
        // subscribed and re-POSTs this export later on (BDA-6060).
        take(1),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((result) => {
        const status: 'ok' | 'error' = result?.status === 'ok' ? 'ok' : 'error';
        this.ref.close(<BdExportAsFileAsyncDialogResult>{ status });
      });
  }
}
